import { Button } from "@/components/ui/button";
import { handleActionError } from "@/lib/errors";
import { useShippingAddressChange } from "@/pages/checkout/hooks";
import { useSellerCart } from "@/queries/cart";
import { useUpdateCartsAddresses } from "@/queries/cart/mutations";
import { paymentProvidersQuery } from "@/queries/payment";
import { collapseProvenance } from "@/utils/cart";
import { getSelectedShippingPickerId } from "@/utils/shippingOption";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { Form } from "@repo/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { BackToCartLink } from "../components/BackToCartLink";
import { CheckoutActionBar } from "../components/CheckoutActionBar";
import { ShippingAddressSection } from "../components/ShippingAddressSection";
import { ShippingMethodSection } from "../components/ShippingMethodSection";
import { useCheckoutAddressState } from "../hooks/useCheckoutAddressState";
import { resolveCheckoutAddresses } from "../resolve-addresses";
import type { CheckoutAddressFormData } from "../validations";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

/** Lets the step's submit button name its form from outside it (desktop). */
export const SHIPPING_FORM_ID = "checkout-shipping-form";

export interface ShippingStepProps {
  /** Cart IDs being checked out */
  cartIds: string[];
  /** Carts being checked out (for shipping method section) */
  carts: StoreSellerCart[];
}

export const ShippingStep = memo(function ShippingStep({
  cartIds,
  carts
}: ShippingStepProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Fetch first cart to read address metadata for pre-selection
  const firstCartId = cartIds[0];
  const { data: cartData, isLoading: cartLoading } = useSellerCart(
    firstCartId,
    { enabled: !!firstCartId }
  );

  // Warm the payment providers while the buyer fills this step in. The query
  // holds a 5-minute staleTime but only the payment step reads it, so it was
  // always cold exactly when it was needed - a request the buyer waits on
  // after clicking "Continue to Payment". Fetching it here costs nothing they
  // can perceive and the next step finds it already cached.
  //
  // Every cart in a checkout carries the same region, so the first answers for
  // all of them - and it comes off the cart list, so this needs no fetch of
  // its own to know what to prefetch.
  const regionId = carts[0]?.region_id;
  useEffect(() => {
    if (!regionId) return;
    void queryClient.prefetchQuery(paymentProvidersQuery(regionId));
  }, [queryClient, regionId]);

  const { form, addresses, isLoading } = useCheckoutAddressState({
    //
    cartData,
    cartLoading
  });

  const updateAddressesMutation = useUpdateCartsAddresses();

  const { handleSubmit, formState } = form;
  const { isSubmitting } = formState;

  // Clear submit error when any cart's shipping method changes. Keyed on the
  // composite picker id (not the bare base id) so switching between two live
  // rates that share one base option id still registers as a change (KAN-108).
  const shippingMethodKey = carts
    .map((c) => getSelectedShippingPickerId(c.shipping_methods[0]) ?? "")
    .join(",");

  useEffect(() => {
    if (submitError) {
      setSubmitError(null);
    }
  }, [shippingMethodKey]);

  const handleShippingAddressChange = useShippingAddressChange({
    cartIds,
    form,
    updateAddressesMutation
  });

  // Handle form submission
  const onSubmit = useCallback(
    async (data: CheckoutAddressFormData) => {
      setSubmitError(null);

      // Validate that all carts have a shipping method selected
      const cartsWithoutShipping = carts.filter(
        (cart) => cart.shipping_methods.length === 0
      );
      if (cartsWithoutShipping.length > 0) {
        const sellerNames = cartsWithoutShipping
          .map((cart) => cart.seller.name)
          .join(", ");

        setSubmitError(
          cartIds.length > 1
            ? `Please select a shipping method for: ${sellerNames}`
            : "Please select a shipping method"
        );

        return;
      }

      // Saved-id / inline / same-as-shipping, for both addresses. The same
      // resolution the payment step submits with - one module, so the two steps
      // of one checkout cannot disagree about what the buyer's form meant.
      const resolved = resolveCheckoutAddresses(data, addresses);
      if (!resolved.ok) {
        setSubmitError(resolved.error);
        return;
      }
      const { shipping: shippingAddressData, billing: billingAddressData } =
        resolved;

      try {
        // Update all carts with the resolved addresses
        const updated = await updateAddressesMutation.mutateAsync({
          cartIds,
          email: data.email,
          shippingAddress: shippingAddressData,
          billingAddress: billingAddressData
        });

        // The address write re-validates each cart's method against the
        // options its new address is offered, and drops one that is no longer
        // among them (api KAN-121) - typically the baseline estimate picked
        // before the address was known, once live rates replace it. The check
        // above read the carts from before that write, so read the server's
        // answer: leaving with a dropped method reached the payment step with
        // shipping "Not selected" (KAN-230). The picker re-selects from the
        // refreshed options; the buyer confirms and continues.
        const droppedSellers = updated
          .filter(({ cart }) => (cart.shipping_methods?.length ?? 0) === 0)
          .map(
            ({ cart }) =>
              carts.find((c) => c.cart_id === cart.id)?.seller.name ?? cart.id
          );
        if (droppedSellers.length > 0) {
          setSubmitError(
            cartIds.length > 1
              ? `Shipping options changed for your address. Check the shipping method for: ${droppedSellers.join(", ")}`
              : "Shipping options changed for your address. Check your shipping method, then continue."
          );
          return;
        }

        // Navigate to review step on success
        navigate({
          to: "/checkout",
          search: {
            cart_ids: cartIds.join(","),
            step: "payment"
          }
        });
      } catch (error) {
        setSubmitError(
          handleActionError(error, {
            boundary: "checkout shipping step",
            fallback: "We couldn't update your addresses. Please try again."
          })
        );
      }
    },
    [addresses, cartIds, carts, navigate, updateAddressesMutation]
  );

  const isPending = isSubmitting || updateAddressesMutation.isPending;

  // Grand total for the mobile action bar - summed from the carts already in
  // hand, so this needs no new prop or fetch.
  const grandTotal = carts.reduce(
    (sum, cart) => sum + (cart.totals?.total ?? 0),
    0
  );

  // Same source as the total it annotates (KAN-105). Recomputed on every cart
  // read, so once the server has restated a cart the marker follows it.
  const grandTotalProvenance = collapseProvenance(
    carts.map((cart) => cart.totals?.provenance)
  );

  return (
    <Form {...form}>
      <form
        id={SHIPPING_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
      >
        {/* Error Alert */}
        {submitError && (
          <div
            className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{submitError}</span>
          </div>
        )}

        {/* Shipping Address Section */}
        <section
          className="rounded-xl border border-gray-200 bg-white"
          aria-labelledby="shipping-address-heading"
        >
          <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">
              1
            </span>
            <h2
              id="shipping-address-heading"
              className="text-lg font-semibold text-gray-900"
            >
              Delivery
            </h2>
          </div>
          <div className="p-6">
            <ShippingAddressSection
              form={form}
              addresses={addresses}
              isLoading={isLoading}
              disabled={isPending}
              onAddressChange={handleShippingAddressChange}
            />
          </div>
        </section>

        {/* Shipping Method Section */}
        <section
          className="rounded-xl border border-gray-200 bg-white"
          aria-labelledby="shipping-method-heading"
        >
          <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">
              2
            </span>
            <h2
              id="shipping-method-heading"
              className="text-lg font-semibold text-gray-900"
            >
              Shipping Method
            </h2>
          </div>
          <div className="p-6">
            <ShippingMethodSection carts={carts} disabled={isPending} />
          </div>
        </section>

        {/* Continue with Back to Cart beneath it, under the total in the order
            summary from lg (inline here if the summary is unavailable); a
            sticky bottom bar below lg (KAN-77). As on the payment step. */}
        <CheckoutActionBar
          total={grandTotal}
          provenance={grandTotalProvenance}
          secondary={<BackToCartLink />}
          primary={
            <Button
              type="submit"
              // On desktop the button is portalled into the order summary,
              // outside this <form>, so it names the form it submits.
              form={SHIPPING_FORM_ID}
              size="lg"
              disabled={isPending || isLoading}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 focus-visible:ring-purple-500"
            >
              {isPending ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  Processing...
                </>
              ) : (
                <>
                  Continue to Payment
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </Button>
          }
        />
      </form>
    </Form>
  );
});
